<template>
  <div class="sign-in-popup">
    <transition
      name="fade"
      mode="out-in">
      <!--   SIGN IN VIEW   -->
      <form
        v-if="currentView === views.SIGN_IN"
        class="sign-in-popup__wrapper"
        @submit.prevent="signIn">
        <h2 class="sign-in-popup__title">
          Log in to SkyCoach
        </h2>

        <transition
          name="fade"
          :duration="{ enter: 1000, leave: 0 }">
          <div
            v-if="errors.email || errors.password"
            :class="{'sign-in-popup__error-shake': isShowError}"
            class="sign-in-popup__error"
            @animationend="isShowError = false">
            <p>
              Oops! We couldn’t find this email or password. Please try again&nbsp;☝️
            </p>

            <div
              class="sign-in-popup__error-close shape-cross"
              @click="errors.email = errors.password = false" />
          </div>
        </transition>

        <ui-input
          v-model="accountData.email"
          placeholder="Email"
          name="email"
          inputmode="email"
          autocomplete="email"
          class="sign-in-popup__form-input"
          :z-index-tooltip="1051"
          focus-on-mount
          @input="errors.email = false" />

        <ui-input
          v-model="accountData.password"
          class="sign-in-popup__form-input"
          placeholder="Password"
          type="password"
          name="password"
          autocomplete="current-password"
          :z-index-tooltip="1051"
          @input="errors.password = false" />

        <button
          class="button button-main sign-in-popup__main-button"
          type="submit">
          Log in
        </button>

        <div
          role="button"
          class="sign-in-popup__forgot-button"
          @click="changeView(views.FORGOT_PASSWORD)">
          Forgot password?
        </div>
      </form>

      <!--   NEW PASSWORD VIEW   -->
      <form
        v-if="currentView === views.NEW_PASSWORD || currentView === views.NEW_PASSWORD_PRO"
        class="sign-in-popup__wrapper sign-in-popup__wrapper--new-password"
        @submit.prevent="saveNewPassword">
        <p
          v-if="orderId"
          class="sign-in-popup__subtitle">
          Thanks for purchase. Your order ID: {{ orderId }}
        </p>

        <h2 class="sign-in-popup__title">
          <template v-if="currentView === views.NEW_PASSWORD">
            Create new password
          </template>
          <template v-if="currentView === views.NEW_PASSWORD_PRO">
            New password
          </template>
        </h2>

        <transition
          name="fade"
          :duration="{ enter: 1000, leave: 0 }">
          <div
            v-if="isShowError && errors.resetPassword"
            class="sign-in-popup__error sign-in-popup__error-shake sign-in-popup__error_reset-password">
            <p>
              {{ errors.resetPassword }}
            </p>

            <div
              class="sign-in-popup__error-close shape-cross"
              @click="isShowError = false" />
          </div>
        </transition>

        <p class="sign-in-popup__subtitle">
          <template v-if="currentView === views.NEW_PASSWORD">
            For account: {{ userEmail }}
          </template>
          <template v-if="currentView === views.NEW_PASSWORD_PRO">
            Please enter new password for your pro cabinet, the login for it will be your email ({{ userEmail }})
          </template>
        </p>

        <ui-input
          v-model="accountData.password"
          type="password"
          placeholder="New password"
          autocomplete="new-password"
          :error="errors.password"
          :z-index-tooltip="zIndexTooltip"
          focus-on-mount
          class="sign-in-popup__form-input"
          @input="errors.password = false" />

        <ui-input
          v-model="accountData.passwordConfirmation"
          type="password"
          placeholder="New password (again)"
          autocomplete="new-password"
          :error="errors.passwordConfirmation"
          :z-index-tooltip="zIndexTooltip"
          class="sign-in-popup__form-input"
          @input="errors.passwordConfirmation = false" />

        <button
          class="button button-main sign-in-popup__main-button"
          type="submit">
          Change password
        </button>
      </form>

      <!--   FORGOT PASSWORD VIEW   -->
      <form
        v-if="currentView === views.FORGOT_PASSWORD"
        class="sign-in-popup__wrapper"
        @submit.prevent="sendPasswordLink">
        <h2 class="sign-in-popup__title">
          Forgot password?
        </h2>

        <p class="sign-in-popup__subtitle">
          Enter your email and we’ll send you a reset link
        </p>

        <ui-input
          v-model="accountData.email"
          class="sign-in-popup__form-input"
          placeholder="Email"
          name="email"
          inputmode="email"
          autocomplete="email"
          :z-index-tooltip="zIndexTooltip"
          :error="errors.email"
          focus-on-mount
          @input="errors.email = false" />

        <button
          v-if="!isPasswordSent"
          class="button button-main sign-in-popup__main-button"
          type="submit">
          Send link
        </button>

        <div
          v-else
          class="sign-in-popup__text">
          Reset link sent!<br>
          If you don't see it in your email,<br> please check your spam folder
        </div>
      </form>

      <!--   JOIN VIEW   -->
      <form
        v-if="currentView === views.JOIN"
        class="sign-in-popup__wrapper"
        @submit.prevent="register">
        <h2 class="sign-in-popup__title">
          Sign up for SkyCoach
        </h2>

        <p class="sign-in-popup__subtitle">
          Enter your email and password to create an account
        </p>

        <!--        <p class="sign-in-popup__account-info">-->
        <!--          Account information-->
        <!--        </p>-->

        <div class="sign-in-popup_grid">
          <ui-input
            v-model="accountData.email"
            class="sign-in-popup__form-input"
            placeholder="Email"
            name="email"
            inputmode="email"
            autocomplete="email"
            :error="errors.email"
            :z-index-tooltip="zIndexTooltip"
            focus-on-mount
            @input="errors.email = false" />

          <ui-input
            v-model="accountData.password"
            class="sign-in-popup__form-input"
            placeholder="Password"
            type="password"
            name="password"
            autocomplete="current-password"
            :error="errors.password"
            :z-index-tooltip="zIndexTooltip"
            @input="errors.password = false" />
        </div>

        <div class="sign-in-popup__notify">
          <p class="sign-in-popup__notify-title">
            Choose one or both notification channels
          </p>

          <div class="sign-in-popup_grid">
            <ui-input
              v-model="accountData.discord"
              class="sign-in-popup__form-input"
              placeholder="Discord name"
              name="discord"
              :icon="{ path: icons.discord }"
              :error="errors.discord"
              :z-index-tooltip="zIndexTooltip"
              @input="errors.discord = false" />

            <ui-input
              v-model="accountData.facebook"
              class="sign-in-popup__form-input"
              placeholder="Facebook ID"
              name="facebook"
              :icon="{ path: icons.facebook }"
              :error="errors.facebook"
              :z-index-tooltip="zIndexTooltip"
              @input="errors.facebook = false" />
          </div>
        </div>

        <div
          class="sign-in-popup__checkboxes">
          <ui-checkbox v-model="isMarketingChecked">
            <span class="sign-in-popup__checkbox-text">
              I want subscriber-only deals, discounts, and promo codes
            </span>
          </ui-checkbox>

          <ui-checkbox v-model="isTermsChecked">
            <span class="sign-in-popup__checkbox-text">
              I have read and agree to the
              <a
                href="/agreement"
                target="_blank"
                class="sign-in-popup__link">
                Terms of Service
              </a>
            </span>
          </ui-checkbox>
        </div>

        <button
          :disabled="!isTermsChecked"
          class="button button-main sign-in-popup__main-button"
          type="submit">
          Sign up
        </button>
      </form>
    </transition>

    <div
      v-if="currentView === views.SIGN_IN"
      class="sign-in-popup__footer">
      <p class="sign-in-popup__footer-text">
        Don’t have an account?
      </p>

      <div
        role="button"
        class="button button-additional sign-in-popup__footer-button"
        @click="changeView(views.JOIN)">
        Sign up
      </div>
    </div>

    <div
      v-else-if="currentView === views.JOIN"
      class="sign-in-popup__footer">
      <p class="sign-in-popup__footer-text">
        Already have an account?
      </p>

      <div
        role="button"
        class="button button-additional sign-in-popup__footer-button"
        @click="changeView(views.SIGN_IN)">
        Log in
      </div>
    </div>

    <div
      v-if="showIntercomButton"
      class="sign-in-popup__intercom">
      <button
        class="sign-in-popup__intercom-button button"
        @click="openChat">
        Chat with support
      </button>
    </div>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import UiInput from 'Components/ui/Input/UiInput.vue';
import googleTagAnalytics from 'Root/drivers/googleTagAnalytics';
import IntercomApiDriver from 'Root/drivers/IntercomApiDriver';
import altcraftAnalytics from 'Root/drivers/altcraftAnalytics';
import UserModel from 'Models/UserModel';
import { regExpDiscord, regExpEmail } from 'Root/helpers/regexps';
import AccountInformationModel from 'Models/AccountInformationModel';
import { getAdvertisingParamsFromSession } from 'Root/helpers/handleAdvertisingParams';
import SignInModel from 'Models/SignInModel';
import ContactModel from 'Models/ContactModel';
import fingerprintHeader from 'Root/mixins/fingerprintHeader';
import * as Sentry from '@sentry/vue';
import UiCheckbox from 'Components/ui/Checkbox/UiCheckbox.vue';

const IntercomErrorMessages = {
  login: 'Hi! I have problems with authorization. Please help me',
  resetPassword: 'Hi! I have password setting error. Please help me',
};

export default {
  name: 'SignInPopup',

  components: {
    UiCheckbox,
    UiInput,
  },

  mixins: [fingerprintHeader],

  props: {
    /**
     * {
     *   initialView: views
     *   email: String
     *   token: String
     *   redirectPathAfterSignIn: String
     * }
     */
    data: {
      type: Object,
      default: () => ({ initialView: SignInModel.POPUP_VIEWS.SIGN_IN }),
    },
  },

  data() {
    return {
      isShowError: false,
      views: SignInModel.POPUP_VIEWS,
      currentView: this.data.initialView || SignInModel.POPUP_VIEWS.SIGN_IN,
      isPasswordSent: false,
      accountData: {
        email: this.data.email || '',
        password: '',
        discord: '',
        facebook: '',
        passwordConfirmation: '',
      },
      loading: false,
      errors: {
        email: false,
        password: false,
        discord: false,
        facebook: false,
        passwordConfirmation: '',
      },
      icons: {
        facebook: AccountInformationModel.CONTACTS.FACEBOOK.icon,
        discord: AccountInformationModel.CONTACTS.DISCORD.icon,
      },
      zIndexTooltip: 2147483010,
      showIntercomButton: false,
      intercomMessage: null,
      isTermsChecked: false,
      isMarketingChecked: false,
    };
  },

  computed: {
    ...mapState(['user']),

    userEmail() {
      return this.$route.query?.email || this.data.email || '';
    },

    orderId() {
      if (this.$route.query?.redirectPathAfterSignIn) {
        return this.$route.query.redirectPathAfterSignIn.substr(15, 6); // берем id после /client/orders/
      }

      return null;
    },
  },

  beforeDestroy() {
    if (this.data.redirectPathAfterSignIn) {
      this.$router.push(this.data.redirectPathAfterSignIn);
    } else if (this.$route.query.signInComponent) {
      this.$router.replace({ query: { signInComponent: undefined } }); /* удаляем из адресной строки */
      delete this.$route.query.signInComponent; /* и из объекта */
    }
  },

  methods: {
    ...mapActions(['getUser', 'logout']),

    dismiss() {
      this.$vuedals.dismiss();
    },

    changeView(view) {
      this.currentView = view;
      Object.keys(this.accountData).forEach((key) => {
        this.errors[key] = false;
        this.accountData[key] = '';
      });
    },

    openChat() {
      googleTagAnalytics.emitItemOpenChatEvent();
      IntercomApiDriver.show();

      if (this.intercomMessage) {
        IntercomApiDriver.showNewMessage(this.intercomMessage);
      }
    },

    disableInterface() {
      this.loading = true;
    },

    enableInterface() {
      this.loading = false;
    },

    validateEmail() {
      if (!this.accountData.email.match(regExpEmail)) {
        this.errors.email = 'Please, enter a valid email';
        return false;
      }
      return true;
    },

    loginRequest() {
      const { email, password } = this.accountData;
      return this.$axios.post('/shop/user/login', { email, password }, this._getFingerprintHeader());
    },

    async signIn() {
      this.isShowError = false;

      if (this.loading || !this.validateEmail()) {
        this.isShowError = true;
        return;
      }

      if (!this.accountData.password) {
        this.errors.password = 'Please, enter a password';
        this.isShowError = true;
        return;
      }

      this.disableInterface();

      const { data } = await this.loginRequest().catch(this.enableInterface);
      if (data.info === 'not authenticated') {
        this.enableInterface();
        this.errors.password = 'User with such email and password was not found';
        this.isShowError = true;
        return;
      }

      if (this.user && this.accountData.email !== this.user.email) {
        IntercomApiDriver.shutdown();
      }

      await this.getUser();

      googleTagAnalytics.fireUserId(this.user.id);

      altcraftAnalytics.firePageView(this.$store.state.user);

      this.dismiss();

      this.$nextTick(() => { /* Нужен, чтобы с beforeDestroy не было конфликтов */
        if (this.data.redirectPathAfterSignIn) {
          this.$router.push(this.data.redirectPathAfterSignIn);
        } else if (UserModel.LOGIN_REDIRECTS[this.user.role]) {
          this.$router.push({ name: UserModel.LOGIN_REDIRECTS[this.user.role] });
        }
      });
    },

    sendPasswordLink() {
      if (this.loading || !this.validateEmail()) {
        return;
      }

      this.$axios.post('/shop/user/send_reset_password_link', { email: this.accountData.email })
        .then(() => {
          this.isPasswordSent = true;
        });
    },

    async register() {
      if (this.loading || !this.validateEmail()) {
        return;
      }

      if (!this.accountData.password) {
        this.errors.password = 'Please, enter a password';
        return;
      }

      if (this.accountData.discord && !regExpDiscord.test(this.accountData.discord)) {
        this.errors.discord = 'Invalid discord username';
        return;
      }
      this.disableInterface();

      const params = {
        email: this.accountData.email,
        password: this.accountData.password,
        contacts: [
          { id: ContactModel.Discord.id, pivot: { value: this.accountData.discord } },
          { id: ContactModel.Facebook.id, pivot: { value: this.accountData.facebook } },
        ],
        meta: {
          advertisingParams: getAdvertisingParamsFromSession(),
        },
        is_marketing_emails_allowed: this.isMarketingChecked,
      };

      const referee = localStorage.getItem('referee');

      if (referee) {
        params.referee = referee;
      }

      const { data } = await this.$axios.post('/shop/user/register', params, this._getFingerprintHeader())
        .catch(console.error);

      if (data.errors?.email) {
        this.errors.email = 'This email already exist. Try to reset your password.';
        this.enableInterface();
        return;
      }

      await this.getUser();

      googleTagAnalytics.fireUserId(this.user.id);

      this.enableInterface();
      this.dismiss();

      this.$nextTick(() => {
        this.$router.push({ name: 'client' });
      });
    },

    async saveNewPassword() {
      /* eslint-disable camelcase */
      if (this.loading) {
        return;
      }

      const {
        password,
        passwordConfirmation,
      } = this.accountData;

      this.errors.password = '';
      this.errors.passwordConfirmation = '';
      this.errors.resetPassword = '';
      this.isShowError = false;

      if (!password) {
        this.errors.password = 'Please, enter a password';
        return;
      }

      if (!passwordConfirmation || password !== passwordConfirmation) {
        this.errors.passwordConfirmation = '';
        this.errors.passwordConfirmation = 'Mismatched passwords';
        return;
      }

      const localToken = localStorage.getItem(UserModel.SET_PASSWORD_TOKEN_NAME);
      const tokenToUse = localToken || this.data.token;

      this.intercomMessage = null;

      if (!this.data.email || !tokenToUse) {
        if (!tokenToUse) {
          Sentry.captureException({
            type: 'token',
            data: this.data,
          });
        }
        this.dismiss();
        return;
      }

      const params = {
        email: this.userEmail,
        token: tokenToUse,
        password,
        password_confirmation: passwordConfirmation,
      };

      this.disableInterface();

      try {
        const resetPassResponse = await this.$axios.post('/shop/user/reset_password', params);
        if (resetPassResponse?.data?.errors
                || resetPassResponse.data !== 'passwords.reset') {
          this.intercomMessage = IntercomErrorMessages.resetPassword;
          this.isShowError = true;

          // SKY-5003: разлогиниваем пользователя, если его токен из LS протух
          if (tokenToUse === localStorage.getItem(UserModel.SET_PASSWORD_TOKEN_NAME)) {
            localStorage.removeItem(UserModel.SET_PASSWORD_TOKEN_NAME);
            await this.logout();

            // eslint-disable-next-line max-len
            this.errors.resetPassword = 'It seems that your password setting token has expired. Please refresh the page and reset your password via email again.';
          } else {
            this.errors.resetPassword = 'Oops! This reset password link is invalid ☝️';
          }

          throw new Error();
        }

        const loginResponse = await this.loginRequest();
        if (loginResponse?.data?.errors) {
          this.intercomMessage = IntercomErrorMessages.login;
          throw new Error();
        }

        await this.getUser();
        localStorage.removeItem(UserModel.SET_PASSWORD_TOKEN_NAME);

        googleTagAnalytics.fireUserId(this.user.id);

        // SKY-5003: если сброс пароля прошел успешно, очищаем query
        if (Object.keys(this.$route.query).length > 0) {
          await this.$router.replace({ query: null });
        }
        this.dismiss();
      } catch (e) {
        console.error(e);
        this.showIntercomButton = true;
        this.intercomMessage = this.intercomMessage || IntercomErrorMessages.login;
        this.isShowError = true;
      } finally {
        this.enableInterface();
      }
      /* eslint-enable */
    },
  },
};
</script>

<style lang="scss">
  .sign-in-popup .mobile-panel-popup__body {
    padding: 0;
  }

  .sign-in-popup.mobile-panel-popup {
    background-color: $color--basic-black;
  }

  @media screen and #{$screen-more-or-640} {
    .sign-in-popup > .vuedal {
      width: auto !important;
    }
  }
</style>

<style lang="scss" scoped>
  .sign-in-popup {
    text-align: center;
    font-weight: 500;
    line-height: 1.2;
  }

  .sign-in-popup__wrapper {
    display: grid;
    grid-row-gap: 16px;
    padding: 5px 20px 38px;
  }

  .sign-in-popup__wrapper--new-password {
    max-width: 500px;
  }

  .sign-in-popup__error {
    display: flex;
    align-items: center;
    justify-content: space-between;
    background-color: $color--preset-bad;
    border-radius: 5px;
    font-size: 13px;
    padding: 16px;
  }

  .sign-in-popup__error-shake {
    animation: shake .5s ease-in-out;
  }

  @keyframes shake {
    0%, 100%{
      transform: translateZ(0);
    }
    20%, 60% {
      transform: translate3d(-10px, 0, 0);
    }
    40%, 80%{
      transform: translate3d(10px, 0, 0);
    }
  }

  .sign-in-popup__error-close {
    width: 13px;
    height: 13px;
    margin-left: 16px;
  }

  .sign-in-popup__checkboxes {
    display: grid;
    grid-gap: 16px;
  }

  .sign-in-popup__checkbox-text {
    font-size: 13px;
    line-height: 16px;
    font-weight: 500;
    text-align: left;
  }

  .sign-in-popup__link {
    font-size: 13px;
    line-height: 16px;
    font-weight: 500;
    color: $color--basic-white;
    text-decoration: underline;
  }

  .sign-in-popup__main-button {
    padding: 12px;
    font-size: 13px;
  }

  .sign-in-popup__footer {
    background-color: $color--basic-dark-gray;
    padding: 24px 0 27px;
  }

  .sign-in-popup__footer-text {
    font-size: 19px;
    margin-bottom: 16px;
  }

  .sign-in-popup__footer-button {
    padding: 12px;
    font-size: 13px;
    width: max-content;
    margin: 0 auto;
  }

  .sign-in-popup__title {
    font-size: 21px;
    line-height: 30px;
  }

  .sign-in-popup__subtitle {
    font-size: 13px;
    color: $color--basic-dark-gray;
  }

  .sign-in-popup__subtitle + .sign-in-popup__title {
    margin-top: -8px;
  }

  .sign-in-popup__title + .sign-in-popup__subtitle {
    margin-top: -8px;
  }

  .sign-in-popup__account-info {
    font-size: 13px;
    letter-spacing: 0.1em;
    text-transform: uppercase;
  }

  .sign-in-popup_grid {
    display: grid;
    grid-gap: 16px;
  }

  .sign-in-popup__notify {
    background: $color--basic-menu;
    border-radius: 16px;
    padding: 16px;
  }

  .sign-in-popup__notify-title {
    font-size: 13px;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: $color--basic-dark-gray;
    margin-bottom: 16px;
  }

  .sign-in-popup__text {
    font-size: 15px;
    color: $color--basic-light-gray;
  }

  .sign-in-popup__forgot-button {
    margin: 0 auto;
    cursor: pointer;
    font-size: 11px;
  }

  .sign-in-popup__form-input {
    --background-color: #{$color--basic-dark-gray} !important;
    --height: 40px !important;
    --padding: 12px 13px 13px !important;
    --icon-color: #{$color--basic-light-gray} !important;
    --icon-margin: 0 16px 0 0 !important;
    --border-radius: 5px !important;
    --font-size: 13px !important;
    --font-weight: 500 !important;
  }

  .sign-in-popup__intercom {
    margin-top: -24px;
    margin-bottom: 40px;
  }

  .sign-in-popup__intercom-button {
    padding: 6px;
    text-transform: uppercase;
    font-size: 11px;
    background-color: $color--basic-blue;
  }

  .fade-enter-active, .fade-leave-active {
    transition: opacity .2s ease-out;
  }

  .fade-enter, .fade-leave-to {
    opacity: 0;
  }

  @media screen and #{$screen-more-or-640} {
    .sign-in-popup__wrapper {
      padding: 0 45px 40px;
    }

    .sign-in-popup__error {
      max-width: 305px;
    }

    .sign-in-popup__error_reset-password {
      max-width: unset;
    }

    .sign-in-popup__title {
      font-size: 25px;
    }

    .sign-in-popup__subtitle {
      font-size: 15px;
    }

    .sign-in-popup__account-info {
      margin-top: 8px;
    }

    .sign-in-popup__main-button {
      width: max-content;
      padding: 15px;
      font-size: 15px;
      margin: 0 auto;
    }

    .sign-in-popup__notify {
      margin: 8px 0;
    }

    .sign-in-popup_grid {
      grid-template-columns: 1fr 1fr;

      .sign-in-popup__form-input {
        min-width: 220px;
      }
    }

    .sign-in-popup__footer {
      border-radius: 0 0 15px 15px;
    }

    .sign-in-popup__form-input {
      --font-size: 15px !important;
      --height: 48px !important;
      --padding: 15px !important;

      min-width: 306px;
    }

    .sign-in-popup_grid .sign-in-popup__form-input {
      min-width: 250px;
    }
  }
</style>
